import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { supabase } from "@/lib/supabase";
import { defaultLandingFor, type UserRole } from "@/lib/auth";
import { Loader } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";

async function landingPathForUser(userId: string): Promise<string> {
  const { data } = await supabase
    .from("users")
    .select("active_role")
    .eq("id", userId)
    .maybeSingle();
  return defaultLandingFor((data?.active_role as UserRole | null) ?? null);
}

/**
 * Landing target for Supabase Auth magic-link redirects. We use one URL
 * for everything that needs "exchange this token for a session" behaviour:
 *   - the email-OTP "click the link" flow
 *   - the Telegram-login flow (returns a magic link from our Edge Function)
 *
 * The Supabase JS client auto-detects the auth token in the URL hash on
 * load and persists the session — we just need to wait for that side
 * effect, then send the user where they actually wanted to be.
 *
 * If `?next=` is provided we honour it (deep-link from a Telegram-bot
 * button to a specific event, for example). Otherwise we look up the
 * user's active_role and apply the shared `defaultLandingFor` rule —
 * the same one VerifyOtpPage and TelegramLoginButton use, so all sign-in
 * paths agree on the landing page.
 */
type OtpType =
  | "magiclink"
  | "email"
  | "recovery"
  | "invite"
  | "signup"
  | "email_change";

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function go(userId: string) {
      const params = new URLSearchParams(window.location.search);
      const explicit = params.get("next");
      const dest = explicit ?? (await landingPathForUser(userId));
      if (!mounted) return;
      navigate(dest, { replace: true });
    }

    async function exchangeTokenHash(tokenHash: string, type: OtpType) {
      const { data, error: verifyErr } = await supabase.auth.verifyOtp({
        type,
        token_hash: tokenHash,
      });
      if (verifyErr) {
        if (mounted) setError(verifyErr.message);
        return;
      }
      const userId = data.user?.id;
      if (userId) await go(userId);
    }

    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get("token_hash");
    const typeParam = (params.get("type") ?? "magiclink") as OtpType;

    if (tokenHash) {
      // Explicit token exchange path — used by the Telegram Login Widget
      // (telegram-login Edge Function passes the hashed_token back, the
      // button forwards us here with it in the URL). Sign out first so a
      // stale session from a previous email login doesn't shadow the new
      // identity we're about to authenticate as.
      void supabase.auth.signOut().finally(() => {
        if (!mounted) return;
        void exchangeTokenHash(tokenHash, typeParam);
      });
    } else {
      // Implicit hash-fragment path — used by email magic-link clicks
      // (Supabase parses `#access_token=...` itself). We just wait for
      // onAuthStateChange to fire SIGNED_IN, then route.
      void supabase.auth.getSession().then(({ data }) => {
        if (!mounted) return;
        if (data.session?.user) void go(data.session.user.id);
      });
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === "SIGNED_IN" && session?.user) {
        void go(session.user.id);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [navigate]);

  if (error) {
    return (
      <MobileFrame>
        <div className="grid min-h-[60vh] place-items-center px-6">
          <div className="max-w-[420px] text-center">
            <p className="text-[18px] font-bold text-[#991b1b]">
              Couldn't sign you in
            </p>
            <p className="mt-2 text-[13px] text-[#6c757d]">{error}</p>
            <a
              href="/sign-in"
              className="mt-4 inline-flex h-10 items-center rounded-[10px] bg-[#2563eb] px-4 text-[13px] font-bold text-white no-underline"
            >
              Back to sign in
            </a>
          </div>
        </div>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame>
      <div className="grid min-h-[60vh] place-items-center">
        <Loader label="Signing you in…" />
      </div>
    </MobileFrame>
  );
}
